import type { PreviewContent, TrackerStage } from './types'

// The pickup (3-step) and delivery (4-step) tracks, as stable stage keys. Labels
// are resolved from tracker.* copy only at render — matching keys, never copy.
export const PICKUP_TRACK: TrackerStage[] = ['placed', 'preparing', 'ready']
export const DELIVERY_TRACK: TrackerStage[] = [
  'placed',
  'preparing',
  'on_the_way',
  'delivered',
]

// The full track for the focused status, before any disabled-stage filtering.
// The preview shows the pickup context by default (3-step track) — per the 7/15
// product decision it only switches to the 4-step delivery track when the
// merchant is editing a delivery-section message on a delivery-capable group.
export const getFullTrack = (
  content: PreviewContent,
  deliveryCapable: boolean
): TrackerStage[] => {
  const isDeliveryContext = content.section === 'delivery' && deliveryCapable
  return isDeliveryContext ? DELIVERY_TRACK : PICKUP_TRACK
}

// Derive the tracker track for the focused status. A middle stage whose statuses
// are all disabled is dropped from the track — the first (Placed) and last
// (Ready / Delivered) stage always render as the track's anchors, regardless of
// their own enabled state (Figma).
export const deriveTrack = (
  fullTrack: TrackerStage[],
  disabledStages: Set<TrackerStage>
): TrackerStage[] =>
  fullTrack.filter((stage, index) => {
    const isEndpoint = index === 0 || index === fullTrack.length - 1
    return isEndpoint || !disabledStages.has(stage)
  })

// Confirmed-only (FR-03): every non-'placed' stage of the full track is disabled,
// so only "Order placed" remains. There is no progress to show, so the card
// renders with NO tracker row at all.
export const isConfirmedOnly = (
  fullTrack: TrackerStage[],
  disabledStages: Set<TrackerStage>
): boolean =>
  fullTrack.every(stage => stage === 'placed' || disabledStages.has(stage))

// iOS connector fill state between dot `index` and dot `index + 1`:
//  - 'full'    once the next dot is also reached (both endpoints complete)
//  - 'partial' when this connector immediately follows the active dot (there is
//    always a next dot, since a connector only exists before the last stage)
//  - 'none'    otherwise
export type ConnectorFill = 'full' | 'partial' | 'none'
export const getConnectorFill = (
  activeIndex: number,
  index: number
): ConnectorFill => {
  if (activeIndex >= index + 1) return 'full'
  if (activeIndex === index) return 'partial'
  return 'none'
}
